import { z } from "zod";

// User roles
export const UserRole = {
  ADMIN: "admin",
  EMPLOYEE: "employee",
  PATIENT: "patient",
} as const;

export type UserRoleType = typeof UserRole[keyof typeof UserRole];

// --- TypeScript interfaces for each table ---
export interface User {
  id: number;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role: UserRoleType;
  phone?: string;
  address?: string;
  bio?: string;
  photo_url?: string;
  specialty?: string;
  verified_2fa: boolean;
  active: boolean;
  preferences?: any;
  created_at: string;
  updated_at: string;
}

export interface Service {
  id: number;
  name: string;
  description: string;
  detailed_description?: string | null; // Detailed description for service detail page (HTML rich text)
  category: string;
  price: number;
  duration: number | null; // Can be null for lodging services
  image_url?: string | null;
  active: boolean;
  show_price: boolean;
  disable_price: boolean;
  show_duration: boolean;
  disable_duration: boolean;
  service_type?: 'clinic_hours' | 'lodging_days' | 'homepage_cards' | 'website_presentation';
  room_capacity?: number | null; // Can be null for clinic services - capacity in persons
  room_count?: number | null; // Can be null for clinic services - number of rooms
  room_identifiers?: string[] | null; // Array of room identifiers/numbers for lodging services
  show_on_homepage?: boolean; // Whether to show this service on homepage cards
  homepage_order?: number; // Order of display on homepage
  check_in_hour?: string | null; // Check-in hour for lodging services
  check_out_hour?: string | null; // Check-out hour for lodging services
  cancellation_policy?: string | null; // Cancellation policy text
  payment_policy?: string | null; // Payment policy text
  amenities?: string[] | null; // Array of amenities for lodging services (includes both standard and custom facilities)
  created_at: string;
}

export interface ServicesGeneralFacilities {
  id: number;
  check_in_hour?: string | null;
  check_out_hour?: string | null;
  cancellation_policy?: string | null;
  payment_policy?: string | null;
  breakfast_included?: boolean;
  wifi_available?: boolean;
  parking_available?: boolean;
  tv_available?: boolean;
  pool_available?: boolean;
  restaurant_available?: boolean;
  coffee_available?: boolean;
  shower_available?: boolean;
  air_conditioning_available?: boolean;
  bathroom_available?: boolean;
  custom_facilities?: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface Patient {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  date_of_birth?: string;
  gender?: 'male' | 'female' | 'other';
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  medical_notes?: string;
  allergies?: string;
  user_id?: string; // UUID from users table
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface Appointment {
  id: number;
  patient_id: number | null;
  user_id?: string | null; // UUID from users table
  employee_id: number;
  service_id: number;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'completed' | 'canceled';
  notes?: string;
  reminder_sent?: boolean;
  canceled_at?: string;
  created_at: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  is_public?: boolean;
}

export interface Employee {
  id: number;
  user_id?: string;
  name: string;
  surname: string;
  email: string;
  phone?: string;
  photo_url?: string;
  bio?: string;
  specialty?: string;
  annual_vacation_days?: number;
  facebook_url?: string;
  linkedin_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  website_url?: string;
  youtube_url?: string;
  researchgate_url?: string;
  orcid_url?: string;
  created_at: string;
  updated_at: string;
}

export interface InsertEmployee {
  name: string;
  surname: string;
  email: string;
  phone?: string;
  photo_url?: string;
  bio?: string;
  specialty?: string;
  annual_vacation_days?: number;
  facebook_url?: string;
  linkedin_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  website_url?: string;
  youtube_url?: string;
  researchgate_url?: string;
  orcid_url?: string;
}

export interface MedicalRecord {
  id: number;
  patient_id: number;
  employee_id: number;
  appointment_id: number;
  notes?: string;
  attachments?: string[];
  created_at: string;
}


export interface File {
  id: number;
  user_id: number;
  record_id: number;
  type?: string;
  url: string;
  name?: string;
  uploaded_at: string;
}

export interface Testimonial {
  id: number;
  user_id?: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  content: string;
  rating: number;
  created_at: string;
  approved: boolean;
  is_anonymous: boolean;
}

export interface BlogPost {
  id: number;
  title: string;
  content: string;
  /** References employees.id (employee_id). If NULL, post is published anonymously. Note: employee_id ≠ user_id */
  author_id?: number;
  author_uuid?: string;
  image_url?: string;
  created_at: string;
  updated_at?: string;
  published: boolean;
  slug: string;
  // SEO Fields
  excerpt?: string;
  published_at?: string;
  seo_title?: string;
  seo_description?: string;
  seo_keywords?: string;
  category?: string;
  tags?: string[];
  reading_time?: number;
  featured?: boolean;
  view_count?: number;
  og_image_url?: string;
  canonical_url?: string;
  // AI-generated SEO fields
  schema_markup?: Record<string, any>; // JSON-LD Schema.org markup
  faq?: Array<{ question: string; answer: string }>; // FAQ array
  eeat_signals?: {
    first_hand_experience?: string;
    unique_perspective?: string;
  }; // E-E-A-T signals
  content_intent?: 'informational' | 'helpful'; // Content intent type
  internal_linking_strategy?: string; // Internal linking instructions
  disclaimer?: string; // Universal disclaimer
}

export interface BlogSocialImage {
  id: number;
  blog_post_id: number;
  image_url: string;
  prompt: string;
  image_index: number;
  reference_image_url?: string | null;
  generated_at: string;
  created_at: string;
  updated_at?: string;
}

export interface BlogIdea {
  id: number;
  title: string;
  slug: string;
  ai_instructions?: string | null;
  target_audience?: string | null;
  tone?: 'professional' | 'casual' | 'friendly' | 'formal' | 'informative' | null;
  accuracy?: 'high' | 'medium' | 'low' | null;
  target_seo_keywords?: string | null;
  long_tail_keywords?: string | null;
  estimated_word_count?: number | null;
  status: 'saved' | 'rejected' | 'converted';
  created_at: string;
  updated_at?: string | null;
  converted_to_post_id?: number | null;
}

export interface InsertBlogIdea {
  title: string;
  slug: string;
  ai_instructions?: string | null;
  target_audience?: string | null;
  tone?: 'professional' | 'casual' | 'friendly' | 'formal' | 'informative' | null;
  accuracy?: 'high' | 'medium' | 'low' | null;
  target_seo_keywords?: string | null;
  long_tail_keywords?: string | null;
  intent?: 'informational' | 'commercial' | 'local' | null;
  suggested_category?: string | null;
  suggested_tags?: string[] | null;
  estimated_word_count?: number | null;
  status?: 'saved' | 'rejected' | 'converted';
  converted_to_post_id?: number | null;
}

export interface InsertBlogSocialImage {
  blog_post_id: number;
  image_url: string;
  prompt: string;
  image_index: number;
  reference_image_url?: string | null;
}

export interface SocialMediaImage {
  id: number;
  idea?: string | null;
  title?: string | null;
  text_content?: string | null;
  image_url: string;
  prompt: string;
  reference_image_url?: string | null;
  generated_at: string;
  created_at: string;
  updated_at?: string;
  created_by?: string | null;
}

export interface InsertSocialMediaImage {
  idea?: string | null;
  title?: string | null;
  text_content?: string | null;
  image_url: string;
  prompt: string;
  reference_image_url?: string | null;
  created_by?: string | null;
}

export interface FacebookPost {
  id: number;
  idea: string;
  content: string;
  hashtags?: string | null;
  image_url?: string | null;
  image_prompt?: string | null;
  created_at: string;
  updated_at?: string;
  created_by?: string | null;
  blog_post_id?: number | null;
  reference_image_url?: string | null;
}

export interface InsertFacebookPost {
  idea: string;
  content: string;
  hashtags?: string | null;
  image_url?: string | null;
  image_prompt?: string | null;
  created_by?: string | null;
  blog_post_id?: number | null;
  reference_image_url?: string | null;
}

export interface ContactMessage {
  id: number;
  name: string;
  email: string;
  subject?: string | null;
  message: string;
  created_at: string;
}

export interface InsertContactMessage {
  name: string;
  email: string;
  subject?: string | null;
  message: string;
}

export interface Page {
  id: number;
  slug: string;
  title: string;
  content: string;
  updated_at: string;
  created_at: string;
}

export interface StaticPage {
  id: number;
  slug: string;
  content: string;
  menu_label: string;
  seo_title: string;
  seo_desc: string;
  seo_keywords?: string;
  og_image_url?: string;
  twitter_image_url?: string;
  canonical_url?: string;
  author_name?: string;
  article_type?: string;
  published_date?: string;
  modified_date?: string;
  reading_time?: number;
  visible: boolean;
  custom: boolean;
  contact_cards?: string;
  services_cards?: string;
  team_cards?: string;
  sort_order?: number;
  is_hub_link?: boolean;
  created_at: string;
}

// Contact Cards types for static pages
export type ContactCardType = 'contact-form' | 'map' | 'address' | 'email' | 'phone' | 'social';

export interface ContactFormFieldConfig {
  enabled: boolean;
  required: boolean;
  icon?: string; // SVG sau emoji pentru iconița câmpului
}

export interface ContactFormConfig {
  enabled: boolean;
  title: string;
  titleIcon?: string; // SVG pentru titlul formularului
  description: string;
  fields: {
    name: ContactFormFieldConfig;
    email: ContactFormFieldConfig;
    phone: ContactFormFieldConfig;
    subject: ContactFormFieldConfig;
    message: ContactFormFieldConfig;
  };
  submitButton?: {
    label?: string;
    icon?: string; // SVG pentru butonul de trimitere
  };
}

export interface ContactCard {
  type: ContactCardType;
  cardType: string; // ContactSpecificCardType pentru fiecare tip
  icon?: string; // SVG sau emoji
  title: string;
  order: number;
  enabled: boolean;
  apply_theme_color?: boolean;
  // Pentru type: 'map' - configurator hartă Google Maps
  mapEmbedUrl?: string; // Doar pentru type: 'map' - URL-ul Google Maps embed
  locationHint?: {
    // Pentru type: 'map' - doar title și text (fără icon și enabled)
    title?: string; // ex: "Locație Centrală"
    text?: string; // ex: "Ușor accesibil cu metroul"
  };
  // Pentru type: 'address' - card standard mini (similar cu email/phone/social)
  addressText?: string; // Doar pentru type: 'address' - textul adresei (opțional, fallback la brandingSettings.address)
  // Pentru type: 'email', 'phone', 'social' - datele sunt preluate automat din Settings
  // Nu sunt necesare câmpuri suplimentare, se folosesc contact_email, contact_phone și social_media din AppSettings
}

export interface ContactCardsData {
  cards: ContactCard[];
  contactForm: ContactFormConfig;
}

// Services Cards types for static pages
export type ServiceCardType = 'service-modern' | 'service-classic' | 'service-minimal' | 'service-elegant' | 'service-premium' | 'lodging-category-card';

export interface ServiceCard {
  service_id: number; // ID din tabela services
  cardType: ServiceCardType;
  order: number;
  enabled: boolean;
  show_on_website: boolean;
  // NOTĂ: image_url se preia automat din tabela services, NU se stochează aici
}

export interface ServicesCardsData {
  cards: ServiceCard[];
  intro_text?: string; // text introductiv pentru secțiune
  sort_by_category?: boolean; // sortare după categorie
  default_category?: string; // categorie implicită pentru filtrare
}

// Team Cards types for static pages
export type TeamCardType = 
  | 'team-modern' 
  | 'team-classic' 
  | 'team-minimal' 
  | 'team-elegant' 
  | 'team-premium'
  | 'team-unified';

export interface TeamCard {
  employee_id: number; // ID din tabela employees
  cardType: TeamCardType;
  order: number;
  enabled: boolean;
  show_on_website: boolean;
  // NOTĂ: photo_url se preia automat din tabela employees, NU se stochează aici
}

export interface TeamCardsData {
  cards: TeamCard[];
  intro_text?: string; // text introductiv pentru echipă
}

export interface Banner {
  id: number;
  slug: string;
  image: string;
  active: boolean;
  full_screen: boolean;
  seo_desc?: string;
  created_at: string;
}

export interface AIPrompt {
  id: string;
  prompt_key: string;
  context: string;
  title: string;
  description?: string | null;
  template: string;
  template_format: 'text' | 'json';
  variables?: Record<string, any> | null;
  is_active: boolean;
  is_system: boolean;
  version: number;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
}

export interface AppSettings {
  id: string;
  logo_url?: string;
  logo_original_url?: string;
  logo_svg_code?: string;
  brand_color?: string;
  dark_mode_enabled: boolean;
  theme_name: string;
  primary_color: string;
  secondary_color: string;
  background_color: string;
  text_color: string;
  font_family: string;
  
  // SEO fields
  app_name?: string;
  meta_keywords?: string;
  meta_description?: string;
  favicon_url?: string;
  favicon_svg_code?: string;
  meta_title?: string;
  og_image_url?: string;
  og_title?: string;
  og_description?: string;
  canonical_url?: string;
  robots_directives?: string;
  
  // Extended SEO fields
  seo_hreflang_languages?: Array<{lang: string; url: string}>;
  seo_meta_author?: string;
  seo_meta_generator?: string;
  seo_content_language?: string;
  seo_meta_referrer?: string;
  seo_robots_max_image_preview?: string;
  seo_robots_max_snippet?: number;
  seo_robots_max_video_preview?: number;
  seo_search_action_url?: string;
  
  // Branding fields
  tagline?: string;
  company_description?: string;
  contact_email?: string;
  contact_phone?: string;
  address?: string;
  social_media?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    linkedin?: string;
    youtube?: string;
  };
  
  // PWA fields
  pwa_enabled?: boolean;
  pwa_short_name?: string;
  pwa_description?: string;
  pwa_theme_color?: string;
  pwa_background_color?: string;
  pwa_orientation?: 'portrait' | 'landscape' | 'portrait-primary' | 'landscape-primary';
  pwa_display?: 'fullscreen' | 'standalone' | 'minimal-ui' | 'browser';
  pwa_scope?: string;
  pwa_start_url?: string;
  pwa_icons?: Array<{
    src: string;
    sizes: string;
    type: string;
    purpose?: 'maskable' | 'any';
  }>;
  pwa_shortcuts?: Array<{
    name: string;
    short_name: string;
    description: string;
    url: string;
    icons?: Array<{
      src: string;
      sizes: string;
    }>;
  }>;
  
  // Analytics fields
  google_analytics_id?: string;
  google_site_verification?: string;
  facebook_pixel_id?: string;
  google_tag_manager_id?: string;
  
  // Language settings
  default_language?: string;
  
  // Currency settings
  currency?: string;
  
  // Design settings
  design_custom?: boolean;
  
  created_at: string;
  updated_at: string;
}

export interface Gallery {
  id: string;
  page_slug: string;
  image_url: string;
  alt_text?: string;
  caption?: string;
  active: boolean;
  sort_order: number;
  uploaded_at: string;
}

export interface HomeSection {
  id: number;
  slug: string;
  title: string;
  description: string;
  visible: boolean;
  sort_order: number;
  content?: object | null; // JSONB - can be array for repeatable sections or object for non-repeatable
  card_type?: string | null; // Card type for all cards in this section
  is_predefined?: boolean; // Marks predefined system sections that cannot be deleted, only deactivated
  created_at: string;
  updated_at: string;
}

// Home Section Images - Multiple images per home section (max 6)
export interface HomeSectionImage {
  id: number;
  home_section_id: number;
  image_url: string;
  alt_text?: string | null;
  caption?: string | null;
  is_primary: boolean;
  is_custom_independent?: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface InsertHomeSectionImage {
  home_section_id: number;
  image_url: string;
  alt_text?: string | null;
  caption?: string | null;
  is_primary?: boolean;
  is_custom_independent?: boolean;
  sort_order?: number;
}

export interface ApiKey {
  id: number;
  service_name: string;
  service_url: string;
  api_key: string;
  created_at: string;
  updated_at: string;
  created_by?: number;
  updated_by?: number;
}

// New block position format (percentage-based)
export interface BlockPosition {
  xPct: number;  // 0 = left edge of slot, 1 = right edge
  yPct: number;  // 0 = top edge of slot, 1 = bottom edge
  widthPct: number;  // 0-1, relative to slot width
  heightPct: number | 'auto';  // 'auto' for TextBlock, number for ImageBlock
  rotation: number;  // Degrees
}

// Legacy position format (pixel-based) - kept for backward compatibility
export interface LegacyHeroPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

export interface Hero {
  id: number;
  is_active: boolean;
  priority: number;
  image_side: 'left' | 'right';
  title: string;
  subtitle?: string;
  description?: string;
  ctas: Array<{
    label: string;
    href: string;
    variant: string;
  }>;
  image_url: string;
  image_alt: string;
  // Legacy format - kept for backward compatibility
  image_position?: LegacyHeroPosition;
  text_position?: LegacyHeroPosition;
  // New format (optional, for future migration)
  image_block_position?: BlockPosition;
  text_block_position?: BlockPosition;
  text_section_shape?: 'none' | 'standard' | 'rounded-modern' | 'minimal' | 'gradient-frame' | 'shadow-prominent' | 'blob-text-1' | 'blob-text-2' | 'blob-text-3' | 'blob-text-4';
  image_section_shape?: 'none' | 'rectangle' | 'rounded' | 'circle' | 'blob-organic' | 'blob-organic-2' | 'blob-organic-3' | 'blob-organic-4' | 'blob-organic-5' | 'curved-asymmetric';
  font_size?: 'very_small' | 'small' | 'medium' | 'large' | 'very_large';
  created_at: string;
  updated_at: string;
}

// Add missing Insert types
export interface InsertBanner {
  slug: string;
  image: string;
  active: boolean;
  full_screen: boolean;
  seo_desc?: string;
}

export interface UpdateUser {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: UserRoleType;
  phone?: string;
  address?: string;
  bio?: string;
  photo_url?: string;
  specialty?: string;
  verified_2fa?: boolean;
  active?: boolean;
  preferences?: any;
}

// Users table
export const users = {
  id: "id",
  email: "email",
  password: "password",
  first_name: "first_name",
  last_name: "last_name",
  role: "role",
  phone: "phone",
  address: "address",
  bio: "bio",
  photo_url: "photo_url",
  specialty: "specialty", // For employees (doctors/specialists)
  verified_2fa: "verified_2fa",
  active: "active",
  preferences: "preferences", // Use JSONB in migration
  created_at: "created_at",
  updated_at: "updated_at",
};

// Services table
export const services = {
  id: "id",
  name: "name",
  description: "description",
  category: "category", // dental, beauty, dermatology
  price: "price", // Stored in cents
  duration: "duration", // Duration in minutes
  image_url: "image_url",
  active: "active",
  created_at: "created_at",
};

// Patients table
export const patients = {
  id: "id",
  first_name: "first_name",
  last_name: "last_name",
  email: "email",
  phone: "phone",
  date_of_birth: "date_of_birth",
  gender: "gender",
  address: "address",
  city: "city",
  postal_code: "postal_code",
  country: "country",
  emergency_contact_name: "emergency_contact_name",
  emergency_contact_phone: "emergency_contact_phone",
  medical_notes: "medical_notes",
  allergies: "allergies",
  user_id: "user_id",
  created_at: "created_at",
  updated_at: "updated_at",
  is_active: "is_active",
};

// Appointments table
export const appointments = {
  id: "id",
  patient_id: "patient_id",
  user_id: "user_id",
  employee_id: "employee_id",
  service_id: "service_id",
  start_time: "start_time",
  end_time: "end_time",
  status: "status", // pending, confirmed, completed, canceled
  notes: "notes",
  reminder_sent: "reminder_sent",
  canceled_at: "canceled_at",
  created_at: "created_at",
  contact_name: "contact_name",
  contact_email: "contact_email",
  contact_phone: "contact_phone",
  is_public: "is_public",
};

// Medical Records table
export const medical_records = {
  id: "id",
  patient_id: "patient_id",
  employee_id: "employee_id",
  appointment_id: "appointment_id",
  notes: "notes",
  attachments: "attachments",
  created_at: "created_at",
};


// Files table
export const files = {
  id: "id",
  user_id: "user_id",
  record_id: "record_id",
  type: "type",
  url: "url",
  name: "name",
  uploaded_at: "uploaded_at",
};

// Testimonials table
export const testimonials = {
  id: "id",
  user_id: "user_id", // Removed .notNull() to allow anonymous testimonials
  first_name: "first_name",
  last_name: "last_name",
  email: "email",
  content: "content",
  rating: "rating", // 1-5 stars
  created_at: "created_at",
  approved: "approved",
  is_anonymous: "is_anonymous",
};

// Blog posts table
export const blog_posts = {
  id: "id",
  title: "title",
  content: "content",
  author_id: "author_id",
  image_url: "image_url",
  created_at: "created_at",
  published: "published",
  slug: "slug",
};

// Blog social images table
export const blog_social_images = {
  id: "id",
  blog_post_id: "blog_post_id",
  image_url: "image_url",
  prompt: "prompt",
  image_index: "image_index",
  reference_image_url: "reference_image_url",
  generated_at: "generated_at",
  created_at: "created_at",
  updated_at: "updated_at",
};

// Social media images table
export const social_media_images = {
  id: "id",
  idea: "idea",
  title: "title",
  text_content: "text_content",
  image_url: "image_url",
  prompt: "prompt",
  reference_image_url: "reference_image_url",
  generated_at: "generated_at",
  created_at: "created_at",
  updated_at: "updated_at",
  created_by: "created_by",
};

// Facebook posts table
export const facebook_posts = {
  id: "id",
  idea: "idea",
  content: "content",
  hashtags: "hashtags",
  image_url: "image_url",
  image_prompt: "image_prompt",
  created_at: "created_at",
  updated_at: "updated_at",
  created_by: "created_by",
};

// Pages table
export const pages = {
  id: "id",
  slug: "slug", // e.g. 'home', 'about', etc.
  title: "title",
  content: "content",
  updated_at: "updated_at",
  created_at: "created_at",
};

// Static Pages table
export const static_pages = {
  id: "id",
  slug: "slug",
  content: "content",
  menu_label: "menu_label",
  seo_title: "seo_title",
  seo_desc: "seo_desc",
  visible: "visible",
  custom: "custom",
  created_at: "created_at",
};


// Banners table
export const banners = {
  id: "id",
  slug: "slug",
  image: "image",
  active: "active",
  full_screen: "full_screen",
  seo_desc: "seo_desc",
  created_at: "created_at",
};

// App Settings table
export const app_settings = {
  id: "id", // UUID primary key
  logo_url: "logo_url", // image URL
  logo_original_url: "logo_original_url", // original logo URL (SVG or unprocessed)
  logo_svg_code: "logo_svg_code", // direct SVG code for logo
  brand_color: "brand_color", // hex color
  dark_mode_enabled: "dark_mode_enabled",
  theme_name: "theme_name",
  primary_color: "primary_color",
  secondary_color: "secondary_color",
  background_color: "background_color",
  text_color: "text_color",
  font_family: "font_family",
  
  // SEO fields
  app_name: "app_name",
  meta_keywords: "meta_keywords",
  meta_description: "meta_description",
  favicon_url: "favicon_url",
  favicon_original_url: "favicon_original_url", // original favicon URL (SVG or unprocessed)
  favicon_svg_code: "favicon_svg_code", // direct SVG code for favicon
  meta_title: "meta_title",
  og_image_url: "og_image_url",
  og_title: "og_title",
  og_description: "og_description",
  canonical_url: "canonical_url",
  robots_directives: "robots_directives",
  
  // Extended SEO fields
  seo_hreflang_languages: "seo_hreflang_languages",
  seo_meta_author: "seo_meta_author",
  seo_meta_generator: "seo_meta_generator",
  seo_content_language: "seo_content_language",
  seo_meta_referrer: "seo_meta_referrer",
  seo_robots_max_image_preview: "seo_robots_max_image_preview",
  seo_robots_max_snippet: "seo_robots_max_snippet",
  seo_robots_max_video_preview: "seo_robots_max_video_preview",
  seo_search_action_url: "seo_search_action_url",
  
  // Branding fields
  tagline: "tagline",
  company_description: "company_description",
  contact_email: "contact_email",
  contact_phone: "contact_phone",
  address: "address",
  social_media: "social_media",
  
  // PWA fields
  pwa_enabled: "pwa_enabled",
  pwa_short_name: "pwa_short_name",
  pwa_description: "pwa_description",
  pwa_theme_color: "pwa_theme_color",
  pwa_background_color: "pwa_background_color",
  pwa_orientation: "pwa_orientation",
  pwa_display: "pwa_display",
  pwa_scope: "pwa_scope",
  pwa_start_url: "pwa_start_url",
  pwa_icons: "pwa_icons",
  pwa_shortcuts: "pwa_shortcuts",
  
  // Analytics fields
  google_analytics_id: "google_analytics_id",
  google_site_verification: "google_site_verification",
  facebook_pixel_id: "facebook_pixel_id",
  google_tag_manager_id: "google_tag_manager_id",
  
  // Language settings
  default_language: "default_language",
  
  // Currency settings
  currency: "currency",
  
  // Design settings
  design_custom: "design_custom",
  
  created_at: "created_at",
  updated_at: "updated_at",
};

// Gallery table
export const gallery = {
  id: "id",
  page_slug: "page_slug", // pentru a grupa imaginile pe pagini/sectiuni
  image_url: "image_url",
  alt_text: "alt_text",
  caption: "caption",
  active: "active",
  sort_order: "sort_order",
  uploaded_at: "uploaded_at",
};

// Home Sections table
export const home_sections = {
  id: "id",
  slug: "slug",
  title: "title",
  description: "description",
  visible: "visible",
  sort_order: "sort_order",
  content: "content",
  card_type: "card_type",
  is_predefined: "is_predefined",
  created_at: "created_at",
  updated_at: "updated_at",
};

// Insert schemas
export const insertUserSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address",
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters",
  }),
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  phone: z.string().optional(),
  role: z.enum(Object.values(UserRole) as [string, ...string[]]),
  address: z.string().optional(),
  bio: z.string().optional(),
  photo_url: z.string().optional(),
  specialty: z.string().optional(),
  verified_2fa: z.boolean().optional(),
  active: z.boolean().optional(),
  preferences: z.any().optional(),
});

export const insertServiceSchema = z.object({
  name: z.string().min(1, "Service name is required"),
  description: z.string().optional(),
  detailed_description: z.string().nullable().optional(),
  category: z.string().min(1, "Category is required").optional().or(z.literal("")).nullable(),
  price: z.number().min(0, "Price must be at least 0"),
  duration: z.number().min(1, "Duration must be at least 1 minute").nullable().optional(),
  image_url: z.string().nullable().optional().or(z.literal("")),
  active: z.boolean().optional(),
  show_price: z.boolean().optional().default(true),
  disable_price: z.boolean().optional().default(false),
  show_duration: z.boolean().optional().default(true),
  disable_duration: z.boolean().optional().default(false),
  service_type: z.enum(['clinic_hours', 'lodging_days', 'website_presentation']).nullable().optional(),
  room_capacity: z.number().min(1, "Room capacity must be at least 1").nullable().optional(),
  room_count: z.number().min(1, "Room count must be at least 1").nullable().optional(),
  room_identifiers: z.array(z.string().min(1, "Room identifier cannot be empty")).nullable().optional(),
  check_in_hour: z.string().nullable().optional(),
  check_out_hour: z.string().nullable().optional(),
  cancellation_policy: z.string().nullable().optional(),
  payment_policy: z.string().nullable().optional(),
  amenities: z.array(z.string()).nullable().optional(),
});

export const insertServicesGeneralFacilitiesSchema = z.object({
  check_in_hour: z.string().nullable().optional(),
  check_out_hour: z.string().nullable().optional(),
  cancellation_policy: z.string().nullable().optional(),
  payment_policy: z.string().nullable().optional(),
  breakfast_included: z.boolean().optional().default(false),
  wifi_available: z.boolean().optional().default(false),
  parking_available: z.boolean().optional().default(false),
  tv_available: z.boolean().optional().default(false),
  pool_available: z.boolean().optional().default(false),
  restaurant_available: z.boolean().optional().default(false),
  coffee_available: z.boolean().optional().default(false),
  shower_available: z.boolean().optional().default(false),
  air_conditioning_available: z.boolean().optional().default(false),
  bathroom_available: z.boolean().optional().default(false),
  custom_facilities: z.array(z.string()).nullable().optional(),
});

export const insertPatientSchema = z.object({
  first_name: z.string().min(1, "First name is required").max(100, "First name must be less than 100 characters"),
  last_name: z.string().min(1, "Last name is required").max(100, "Last name must be less than 100 characters"),
  email: z.string().email("Invalid email format").max(255, "Email must be less than 255 characters"),
  phone: z.string().min(1, "Phone number is required").max(20, "Phone number must be less than 20 characters"),
  date_of_birth: z.string().optional(),
  gender: z.enum(['male', 'female', 'other']).optional(),
  address: z.string().optional(),
  city: z.string().max(100, "City must be less than 100 characters").optional(),
  postal_code: z.string().max(10, "Postal code must be less than 10 characters").optional(),
  country: z.string().max(100, "Country must be less than 100 characters").default('Romania'),
  emergency_contact_name: z.string().max(200, "Emergency contact name must be less than 200 characters").optional(),
  emergency_contact_phone: z.string().max(20, "Emergency contact phone must be less than 20 characters").optional(),
  medical_notes: z.string().optional(),
  allergies: z.string().optional(),
  user_id: z.string().optional(), // UUID from users table
  is_active: z.boolean().default(true),
});

export const insertAppointmentSchema = z.object({
  patient_id: z.number().nullable().optional(), // This will be set by the backend
  user_id: z.string().nullable().optional(), // UUID from users table - set by backend
  employee_id: z.number().optional(), // This will be set by the backend
  service_id: z.number().optional(), // This will be set by the backend
  start_time: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid start time",
  }),
  end_time: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid end time",
  }),
  status: z.enum(['pending', 'confirmed', 'completed', 'canceled']).optional(),
  notes: z.string().optional(),
  reminder_sent: z.boolean().optional(),
  canceled_at: z.string().optional(),
  contact_name: z.string().optional(),
  contact_email: z.string().email().optional(),
  contact_phone: z.string().optional(),
  is_public: z.boolean().optional(),
});

export const insertTestimonialSchema = z.object({
  user_id: z.number().optional(), // This will be set by the backend for logged-in users
  first_name: z.string().nullable().optional(), // For anonymous testimonials - can be null
  last_name: z.string().nullable().optional(), // For anonymous testimonials - can be null
  email: z.string().email("Invalid email format").nullable().optional().or(z.literal("")), // For anonymous testimonials - can be null
  content: z.string().min(10, "Content must be at least 10 characters long"),
  rating: z.number().min(1).max(5, "Rating must be between 1 and 5"),
  approved: z.boolean().optional(),
  is_anonymous: z.boolean().optional().default(false),
});

export const insertBlogPostSchema = z.object({
  title: z.string().min(1, "Title is required"),
  content: z.string().min(1, "Content is required"),
  author_id: z.number().nullable().optional(), // This will be set by the backend, null for anonymous posts
  author_uuid: z.string().uuid().optional(),
  image_url: z.string().optional().or(z.literal("")),
  published: z.boolean().optional(),
  slug: z.string().optional(),
  // SEO Fields
  excerpt: z.string().optional(),
  published_at: z.string().datetime().optional(),
  seo_title: z.string().max(100).optional(),
  seo_description: z.string().max(160).optional(),
  seo_keywords: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((val) => (Array.isArray(val) ? val.join(', ') : val)),
  category: z.string().max(50).optional(),
  tags: z.array(z.string()).optional(),
  reading_time: z.number().min(0).optional(),
  featured: z.boolean().optional(),
  view_count: z.number().min(0).optional(),
  og_image_url: z.string().url().optional().or(z.literal("")),
  canonical_url: z.string().url().optional().or(z.literal("")),
  // AI-generated SEO fields
  schema_markup: z.record(z.any()).optional(), // JSON-LD Schema.org markup
  faq: z.array(z.object({
    question: z.string(),
    answer: z.string()
  })).optional(), // FAQ array
  eeat_signals: z.object({
    first_hand_experience: z.string().optional(),
    unique_perspective: z.string().optional()
  }).optional(), // E-E-A-T signals
  content_intent: z.enum(['informational', 'helpful']).optional(), // Content intent type
  internal_linking_strategy: z.string().optional(), // Internal linking instructions
  disclaimer: z.string().optional(), // Universal disclaimer
});

export const insertBlogIdeaSchema = z.object({
  title: z.string().min(1, "Title is required").max(500),
  slug: z.string().min(1, "Slug is required").max(500),
  ai_instructions: z.string().optional().nullable(),
  target_audience: z.string().max(100).optional().nullable(),
  tone: z.enum(['professional', 'casual', 'friendly', 'formal', 'informative']).optional().nullable(),
  accuracy: z.enum(['high', 'medium', 'low']).optional().nullable(),
  target_seo_keywords: z.string().max(500).optional().nullable(),
  long_tail_keywords: z.string().max(500).optional().nullable(),
  estimated_word_count: z.number().int().positive().optional().nullable(),
  status: z.enum(['saved', 'rejected', 'converted']).optional().default('saved'),
  converted_to_post_id: z.number().int().positive().optional().nullable(),
});

export const insertBlogSocialImageSchema = z.object({
  blog_post_id: z.number().int().positive(),
  image_url: z.string().url(),
  prompt: z.string().min(1, "Prompt is required"),
  image_index: z.number().int().min(1).max(5),
  reference_image_url: z.string().url().optional().nullable().or(z.literal("")),
});


// Custom schema for static pages to ensure proper validation
export const insertStaticPageSchema = z.object({
  slug: z.string().min(1, "Slug is required"),
  content: z.string().optional(),
  menu_label: z.string().min(1, "Menu label is required"),
  seo_title: z.string().min(1, "SEO title is required"),
  seo_desc: z.string().min(1, "SEO description is required"),
  visible: z.boolean().default(true),
  custom: z.boolean().default(false),
  contact_cards: z.string().optional(),
  services_cards: z.string().optional(),
  team_cards: z.string().optional(),
  sort_order: z.number().int().min(0).optional(),
  is_hub_link: z.boolean().default(false).optional(),
});

// Banner schema
export const insertBannerSchema = z.object({
  slug: z.string().min(1, "Slug is required"),
  image: z.string().min(1, "Image is required"),
  active: z.boolean().default(true),
  full_screen: z.boolean().default(false),
  seo_desc: z.string().optional(),
});

// Hex color validation regex
const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

// App Settings schema with validation
export const insertAppSettingsSchema = z.object({
  // Existing fields
  logo_url: z.string().nullable().optional().refine(
    (val) => !val || val === '' || z.string().url().safeParse(val).success,
    { message: "Please enter a valid URL" }
  ),
  logo_original_url: z.string().nullable().optional().refine(
    (val) => !val || val === '' || z.string().url().safeParse(val).success,
    { message: "Please enter a valid URL" }
  ),
  logo_svg_code: z.string().nullable().optional(),
  brand_color: z.string().regex(hexColorRegex, "Please enter a valid hex color (e.g., #3B82F6)").nullable().optional(),
  theme_name: z.string().min(1, "Theme name is required").default("default"),
  primary_color: z.string().regex(hexColorRegex, "Please enter a valid hex color (e.g., #3B82F6)").default("#3B82F6"),
  secondary_color: z.string().regex(hexColorRegex, "Please enter a valid hex color (e.g., #10B981)").default("#10B981"),
  background_color: z.string().regex(hexColorRegex, "Please enter a valid hex color (e.g., #FFFFFF)").default("#FFFFFF"),
  text_color: z.string().regex(hexColorRegex, "Please enter a valid hex color (e.g., #1F2937)").default("#1F2937"),
  font_family: z.string().min(1, "Font family is required").default("Inter"),
  dark_mode_enabled: z.boolean().default(false),
  design_custom: z.boolean().default(false).optional(),
  
  // SEO fields
  app_name: z.string().max(100, "App name must be less than 100 characters").nullable().optional(),
  meta_keywords: z.string().max(500, "Meta keywords must be less than 500 characters").nullable().optional(),
  meta_description: z.string().max(220, "Meta description should not exceed 220 characters").nullable().optional(),
  favicon_url: z.string().nullable().optional().refine(
    (val) => !val || val === '' || z.string().url().safeParse(val).success,
    { message: "Please enter a valid URL" }
  ),
  favicon_original_url: z.string().nullable().optional().refine(
    (val) => !val || val === '' || z.string().url().safeParse(val).success,
    { message: "Please enter a valid URL" }
  ),
  favicon_svg_code: z.string().nullable().optional(),
  meta_title: z.string().max(60, "Meta title should not exceed 60 characters").nullable().optional(),
  og_image_url: z.string().optional().refine(
    (val) => !val || val === '' || z.string().url().safeParse(val).success,
    { message: "Please enter a valid URL" }
  ),
  og_title: z.string().max(100, "OG title must be less than 100 characters").nullable().optional(),
  og_description: z.string().max(300, "OG description must be less than 300 characters").nullable().optional(),
  canonical_url: z.string().optional().refine(
    (val) => !val || val === '' || z.string().url().safeParse(val).success,
    { message: "Please enter a valid URL" }
  ),
  robots_directives: z.string().max(200, "Robots directives must be less than 200 characters").nullable().optional(),
  
  // Extended SEO fields
  seo_hreflang_languages: z.array(z.object({
    lang: z.string().max(10, "Language code must be less than 10 characters").optional(),
    url: z.string().optional(),
  })).optional().refine(
    (val) => {
      if (!val || val.length === 0) return true;
      // Filter out empty entries (both lang and url empty or whitespace)
      const validEntries = val.filter(item => 
        item.lang && item.lang.trim().length > 0 && 
        item.url && item.url.trim().length > 0
      );
      // If there are valid entries, they must all have valid format
      if (validEntries.length > 0) {
        return validEntries.every(item => 
          item.lang && item.lang.trim().length >= 2 && 
          item.url && z.string().url().safeParse(item.url.trim()).success
        );
      }
      return true; // Empty entries are allowed
    },
    { message: "All hreflang entries must have a valid language code (min 2 chars) and a valid URL, or be empty" }
  ),
  seo_meta_author: z.string().max(200, "Meta author must be less than 200 characters").nullable().optional(),
  seo_meta_generator: z.string().max(200, "Meta generator must be less than 200 characters").nullable().optional(),
  seo_content_language: z.string().max(10, "Content language must be less than 10 characters").nullable().optional(),
  seo_meta_referrer: z.enum([
    'no-referrer',
    'no-referrer-when-downgrade',
    'origin',
    'origin-when-cross-origin',
    'same-origin',
    'strict-origin',
    'strict-origin-when-cross-origin',
    'unsafe-url'
  ]).nullable().optional(),
  seo_robots_max_image_preview: z.enum(['large', 'standard', 'none']).nullable().optional(),
  seo_robots_max_snippet: z.union([
    z.number().int("Max snippet must be an integer").min(-1, "Max snippet must be at least -1").max(1000, "Max snippet must be less than 1000 characters"),
    z.null(),
    z.undefined()
  ]).optional(),
  seo_robots_max_video_preview: z.union([
    z.number().int().positive("Max video preview must be a positive integer").max(600, "Max video preview must be less than 600 seconds"),
    z.null(),
    z.undefined()
  ]).optional(),
  seo_search_action_url: z.string().max(500, "Search action URL must be less than 500 characters").nullable().optional().refine(
    (val) => !val || val === '' || val.includes('{search_term_string}'),
    { message: "Search action URL must contain {search_term_string} placeholder" }
  ),
  
  // Branding fields
  tagline: z.string().max(200, "Tagline must be less than 200 characters").nullable().optional(),
  company_description: z.string().max(1000, "Company description must be less than 1000 characters").nullable().optional(),
  contact_email: z.string().nullable().optional().refine(
    (val) => !val || val === '' || z.string().email().safeParse(val).success,
    { message: "Please enter a valid email" }
  ),
  contact_phone: z.string().max(20, "Contact phone must be less than 20 characters").nullable().optional(),
  address: z.string().max(500, "Address must be less than 500 characters").nullable().optional(),
  social_media: z.object({
    facebook: z.string().optional().refine(
      (val) => !val || val === '' || z.string().url().safeParse(val).success,
      { message: "Please enter a valid Facebook URL" }
    ),
    instagram: z.string().optional().refine(
      (val) => !val || val === '' || z.string().url().safeParse(val).success,
      { message: "Please enter a valid Instagram URL" }
    ),
    twitter: z.string().optional().refine(
      (val) => !val || val === '' || z.string().url().safeParse(val).success,
      { message: "Please enter a valid Twitter URL" }
    ),
    linkedin: z.string().optional().refine(
      (val) => !val || val === '' || z.string().url().safeParse(val).success,
      { message: "Please enter a valid LinkedIn URL" }
    ),
    youtube: z.string().optional().refine(
      (val) => !val || val === '' || z.string().url().safeParse(val).success,
      { message: "Please enter a valid YouTube URL" }
    ),
  }).nullable().optional(),
  
  // PWA fields
  pwa_enabled: z.boolean().default(false),
  pwa_short_name: z.string().max(50, "PWA short name must be less than 50 characters").optional(),
  pwa_description: z.string().max(300, "PWA description must be less than 300 characters").optional(),
  pwa_theme_color: z.string().regex(hexColorRegex, "Please enter a valid hex color (e.g., #3B82F6)").optional(),
  pwa_background_color: z.string().regex(hexColorRegex, "Please enter a valid hex color (e.g., #FFFFFF)").optional(),
  pwa_orientation: z.enum(['portrait', 'landscape', 'portrait-primary', 'landscape-primary']).optional(),
  pwa_display: z.enum(['fullscreen', 'standalone', 'minimal-ui', 'browser']).optional(),
  pwa_scope: z.string().max(100, "PWA scope must be less than 100 characters").optional(),
  pwa_start_url: z.string().max(100, "PWA start URL must be less than 100 characters").optional(),
  pwa_icons: z.array(z.object({
    src: z.string().url("Please enter a valid icon URL"),
    sizes: z.string().min(1, "Icon sizes are required"),
    type: z.string().min(1, "Icon type is required"),
    purpose: z.enum(['maskable', 'any']).optional(),
  })).optional(),
  pwa_shortcuts: z.array(z.object({
    name: z.string().min(1, "Shortcut name is required").max(50, "Shortcut name must be less than 50 characters"),
    short_name: z.string().min(1, "Shortcut short name is required").max(30, "Shortcut short name must be less than 30 characters"),
    description: z.string().min(1, "Shortcut description is required").max(200, "Shortcut description must be less than 200 characters"),
    url: z.string().min(1, "Shortcut URL is required").max(100, "Shortcut URL must be less than 100 characters"),
    icons: z.array(z.object({
      src: z.string().url("Please enter a valid icon URL"),
      sizes: z.string().min(1, "Icon sizes are required"),
    })).optional(),
  })).optional(),
  
  // Analytics fields
  google_analytics_id: z.string().regex(/^G-[A-Z0-9]{10}$/, "Please enter a valid Google Analytics 4 ID (format: G-XXXXXXXXXX)").optional(),
  google_site_verification: z.string().min(1, "Google Site Verification code is required").optional(),
  facebook_pixel_id: z.string().regex(/^\d{15,16}$/, "Please enter a valid Facebook Pixel ID").optional(),
  google_tag_manager_id: z.string().regex(/^GTM-[A-Z0-9]{6,7}$/, "Please enter a valid Google Tag Manager ID (format: GTM-XXXXXXX)").optional(),
  
  // Language settings
  default_language: z.enum(['ro', 'en']).optional(),
  
  // Currency settings
  currency: z.enum(['EUR', 'USD', 'RON', 'GBP', 'CHF', 'CAD', 'AUD', 'JPY', 'CNY'])
    .default('RON')
    .optional(),
}).refine((data) => {
  // Additional validation: ensure theme colors are consistent
  if (data.theme_name && data.theme_name !== 'default') {
    // If a specific theme is selected, validate that all theme fields are present
    return data.primary_color && data.secondary_color && data.background_color && data.text_color && data.font_family;
  }
  return true;
}, {
  message: "All theme fields are required when selecting a specific theme",
  path: ["theme_name"]
});

// Gallery schema
export const insertGallerySchema = z.object({
  page_slug: z.string().min(1, "Page slug is required"),
  image_url: z.string().min(1, "Image URL is required"),
  alt_text: z.string().optional(),
  caption: z.string().optional(),
  active: z.boolean().default(true),
  sort_order: z.number().optional(),
});

// Home Section schema
export const insertHomeSectionSchema = z.object({
  slug: z.string().min(1, "Slug is required"),
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  visible: z.boolean().default(true),
  sort_order: z.number().default(0),
  content: z.any().optional(), // JSONB - can be array for repeatable sections or object for non-repeatable
  card_type: z.string().optional(), // Card type for all cards in this section
  is_predefined: z.boolean().optional().default(false), // Marks predefined system sections that cannot be deleted, only deactivated
});

// Home Section Image schema
export const insertHomeSectionImageSchema = z.object({
  home_section_id: z.number().int().positive(),
  image_url: z.string().url(),
  alt_text: z.string().optional().nullable(),
  caption: z.string().optional().nullable(),
  is_primary: z.boolean().default(false),
  is_custom_independent: z.boolean().optional().default(false),
  sort_order: z.number().int().min(0).default(0),
});

// Auth schemas
export const loginSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address",
  }),
  password: z.string().min(8, {
    message: "Password must be at least 8 characters",
  }),
});

export const registerSchema = insertUserSchema.pick({
  email: true,
  password: true,
  first_name: true,
  last_name: true,
  phone: true,
  role: true,
}).extend({
  password: z.string().min(8, {
    message: "Password must be at least 8 characters",
  }),
  confirmPassword: z.string(),
  secretCode: z.string().optional(),
}).refine(data => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

// AI Routes Schemas
export const generateBannerSchema = z.object({
  bannerTitle: z.string().min(1, 'Banner title is required'),
  bannerText: z.string().optional(),
  imageUrl: z.string().url('Invalid image URL').optional(),
  imagePosition: z.enum(['center', 'top-left', 'top-right', 'bottom-left', 'bottom-right', 'top', 'bottom', 'left', 'right']).optional(),
  imageSize: z.enum(['small', 'medium', 'large']).optional(),
  imageOpacity: z.number().min(0).max(100).optional(),
  primaryColor: z.string().regex(/^#[0-9A-F]{6}$/i, 'Invalid color format. Use hex format like #FF0000').optional(),
  formatType: z.string().optional(),
  bannerSize: z.enum(['small', 'medium', 'large', 'extra-large']).optional()
});

export const generateBackgroundSchema = z.object({
  backgroundColor: z.string().min(1, 'Background color is required'),
  style: z.string().min(1, 'Style is required')
});

export const generateContentSchema = z.object({
  instructions: z.string().min(1, 'Instructions are required'),
  wordCount: z.number().int().min(100).max(10000).default(1000).optional(),
  tone: z.enum(['professional', 'casual', 'friendly', 'formal', 'informative']).default('professional').optional(),
  accuracy: z.enum(['high', 'medium', 'low']).default('high').optional(),
  inspirationLinks: z.array(z.string().url()).default([]).optional(),
  targetSeoKeywords: z.string().optional(),
  longTailKeywords: z.string().optional(),
  author_id: z.number().int().positive().optional(),
  language: z.string().optional(),
  useAppReferences: z.boolean().default(false).optional(),
  fields: z.record(z.any()).refine((fields) => {
    return fields.slug && fields.title && fields.description;
  }, 'Fields must include slug, title, and description')
});

export const aiChatSchema = z.object({
  message: z.string().min(1, 'Message is required'),
  model: z.number().positive('Model ID must be a positive number').optional(),
  context: z.string().optional()
});

export const seoOptimizeSchema = z.object({
  slug: z.string().min(1, 'Slug is required'),
  seoTitle: z.string().min(1, 'SEO Title is required'),
  seoDescription: z.string().min(1, 'SEO Description is required'),
  content: z.string().min(1, 'Content is required')
});

export const staticPageOptimizeSchema = z.object({
  slug: z.string().min(1, 'Slug is required'),
  menu_label: z.string().min(1, 'Menu label is required'),
  seo_title: z.string().optional(),
  seo_desc: z.string().optional(),
  content: z.string().min(1, 'Content is required')
});

export const contentOptimizeSchema = z.object({
  content: z.string().min(1, 'Content is required')
});

export const suggestInternalLinksSchema = z.object({
  content: z.string().min(1, 'Content is required'),
  currentSlug: z.string().optional(),
  language: z.enum(['ro', 'en']).optional()
});

// AI Response Types
export interface BannerGenerationResponse {
  success: boolean;
  data?: {
    svg: string;
    prompt: string;
    model: string;
  };
  error?: string;
}

export interface BackgroundGenerationResponse {
  success: boolean;
  data?: {
    imageUrl: string;
    prompt: string;
    model: string;
  };
  error?: string;
}

export interface ContentGenerationResponse {
  success: boolean;
  data?: {
    content: string;
    slug: string;
    title: string;
    description: string;
    imagePrompt: string;
    model: string;
    tokens: number;
  };
  error?: string;
}

export interface AIChatResponse {
  success: boolean;
  data?: {
    message: string;
    model: string;
    tokens: number;
  };
  error?: string;
}

export interface SEOOptimizationResponse {
  success: boolean;
  data?: {
    optimizedData: {
      slug: string;
      seoTitle: string;
      seoDescription: string;
      content: string;
    };
    suggestions: string[];
    score: number;
  };
  error?: string;
}

export interface StaticPageOptimizationResponse {
  success: boolean;
  data?: {
    optimizedData: {
      optimizedSlug: string;
      optimizedMenuLabel: string;
      optimizedSeoTitle: string;
      optimizedSeoDescription: string;
      optimizedContent: string;
    };
  };
  error?: string;
}

export interface ContentOptimizationResponse {
  success: boolean;
  data?: {
    optimizedContent: string;
  };
  error?: string;
}


// Export types
export type InsertUser = z.infer<typeof insertUserSchema>;
export type InsertPatient = z.infer<typeof insertPatientSchema>;
export type InsertService = z.infer<typeof insertServiceSchema>;
export type InsertAppointment = z.infer<typeof insertAppointmentSchema>;
export type InsertTestimonial = z.infer<typeof insertTestimonialSchema>;
export type InsertBlogPost = z.infer<typeof insertBlogPostSchema>;
export type InsertStaticPage = z.infer<typeof insertStaticPageSchema>;
export type InsertHomeSection = z.infer<typeof insertHomeSectionSchema>;
export type AppSettingsWithId = z.infer<typeof insertAppSettingsSchema>;
export type Login = z.infer<typeof loginSchema>;
export type Register = z.infer<typeof registerSchema>;

export const insertApiKeySchema = z.object({
  service_name: z.string().min(1, "Service name is required"),
  service_url: z.string().url("Please enter a valid URL"),
  api_key: z.string().min(1, "API key is required"),
});

export const insertHeroSchema = z.object({
  is_active: z.boolean().default(true),
  priority: z.number().int().min(0).default(100),
  image_side: z.enum(['left', 'right']).default('right'),
  title: z.string().min(1, "Title is required"),
  subtitle: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  ctas: z.array(z.object({
    label: z.string().min(1, "CTA label is required"),
    href: z.string().min(1, "CTA href is required"),
    variant: z.string().default('primary')
  })).default([]),
  image_url: z.string().min(1, "Image URL is required"),
  image_alt: z.string().min(1, "Image alt text is required"),
  // Legacy format - kept for backward compatibility
  image_position: z.object({
    x: z.number().default(0),
    y: z.number().default(0),
    width: z.number().default(400),
    height: z.number().default(300),
    rotation: z.number().default(0)
  }).optional(),
  text_position: z.object({
    x: z.number().default(0),
    y: z.number().default(0),
    width: z.number().default(600),
    height: z.number().default(400),
    rotation: z.number().default(0)
  }).optional(),
  // New format (optional, for future migration)
  image_block_position: z.object({
    xPct: z.number().min(0).max(1).default(0),
    yPct: z.number().min(0).max(1).default(0),
    widthPct: z.number().min(0).max(1).default(0.8),
    heightPct: z.union([z.number().min(0).max(1), z.literal('auto')]).default(0.6),
    rotation: z.number().default(0)
  }).optional(),
  text_block_position: z.object({
    xPct: z.number().min(0).max(1).default(0),
    yPct: z.number().min(0).max(1).default(0),
    widthPct: z.number().min(0).max(1).default(0.8),
    heightPct: z.union([z.number().min(0).max(1), z.literal('auto')]).default('auto'),
    rotation: z.number().default(0)
  }).optional(),
  text_section_shape: z.enum([
    'none', // No shape
    'standard', 'rounded-modern', 'minimal', 'gradient-frame', 'shadow-prominent', // Forme inspirate de carduri
    'blob-text-1', 'blob-text-2', 'blob-text-3', 'blob-text-4' // Forme blob pentru text
  ]).default('standard'),
  image_section_shape: z.enum([
    'none', // No shape
    'rectangle', 'rounded', 'circle', // Forme clasice
    'blob-organic', 'blob-organic-2', 'blob-organic-3', 'blob-organic-4', 'blob-organic-5', 'curved-asymmetric' // Forme blob organice
  ]).default('rounded'),
  font_size: z.enum(['very_small', 'small', 'medium', 'large', 'very_large']).default('medium').optional(),
});

export const updateHeroSchema = insertHeroSchema.partial();

// --- Employee Calendar Types ---

// Calendar Availability - represents effective availability intervals
export interface CalendarAvailability {
  start_timestamp: string;
  end_timestamp: string;
  day_of_week: number;
  is_exception: boolean;
  exception_type?: string;
  exception_reason?: string;
}

// Calendar Event - for FullCalendar.js integration
export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  extendedProps: {
    type: 'availability' | 'exception' | 'appointment';
    dayOfWeek: number;
    exceptionType?: string;
    exceptionReason?: string;
    appointmentId?: number;
  };
}

// Exception - for employee availability exceptions
export interface Exception {
  id: number;
  employee_id: number;
  exception_type: 'vacation' | 'sick_leave' | 'holiday' | 'custom' | 'paid_leave' | 'leave_with_recovery' | 'recovery_for_leave' | 'leave' | 'unpaid_leave';
  start_date: string;
  end_date: string;
  start_time?: string;
  end_time?: string;
  is_all_day: boolean;
  reason?: string;
  created_at: string;
  updated_at: string;
}

// Insert Exception - for creating new exceptions (defined as type from schema below)

// Calendar Summary - for statistics and overview
export interface CalendarSummary {
  employee_id: number;
  start_date: string;
  end_date: string;
  total_available_hours: number;
  total_exception_hours: number;
  total_appointment_hours: number;
  available_days: number;
  exception_days: number;
  appointment_days: number;
  exceptions_by_type: {
    vacation: number;
    sick_leave: number;
    holiday: number;
    custom: number;
    paid_leave: number;
    leave_with_recovery: number;
    recovery_for_leave: number;
    leave: number;
    unpaid_leave: number;
  };
}

// Zod schemas for validation
export const insertExceptionSchema = z.object({
  employee_id: z.number().int().positive("Employee ID must be a positive integer"),
  exception_type: z.enum(['vacation', 'sick_leave', 'holiday', 'custom', 'paid_leave', 'leave_with_recovery', 'recovery_for_leave', 'leave', 'unpaid_leave'], {
    errorMap: () => ({ message: "Exception type must be one of: vacation, sick_leave, holiday, custom, paid_leave, leave_with_recovery, recovery_for_leave, leave, unpaid_leave" })
  }),
  start_date: z.string().datetime("Start date must be a valid date"),
  end_date: z.string().datetime("End date must be a valid date"),
  start_time: z.string().optional(),
  end_time: z.string().optional(),
  is_all_day: z.boolean().default(false),
  reason: z.string().optional(),
});

export const updateExceptionSchema = insertExceptionSchema.partial();

export const calendarQuerySchema = z.object({
  startDate: z.string().datetime("Start date must be a valid date"),
  endDate: z.string().datetime("End date must be a valid date"),
});

export type InsertApiKey = z.infer<typeof insertApiKeySchema>;
export type InsertHero = z.infer<typeof insertHeroSchema>;
export type UpdateHero = z.infer<typeof updateHeroSchema>;
export type InsertException = z.infer<typeof insertExceptionSchema>;
export type UpdateException = z.infer<typeof updateExceptionSchema>;
export type CalendarQuery = z.infer<typeof calendarQuerySchema>;

// Public appointments schemas
export const availabilityQuerySchema = z.object({
  employeeId: z.string().transform((val) => parseInt(val)).refine((val) => !isNaN(val) && val > 0, {
    message: "Employee ID must be a valid positive number"
  }),
  date: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid date format"
  })
});

export const contactFormSchema = z.object({
  name: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().optional(),
  subject: z.string().optional(),
  message: z.string().optional(),
  turnstileToken: z.string().optional(),
}).refine((data) => {
  // At least one field must be provided
  return data.name || data.email || data.phone || data.message;
}, {
  message: 'At least one field must be provided',
});

export const publicAppointmentSchema = z.object({
  employee_id: z.number().int().positive("Employee ID must be a positive integer"),
  service_id: z.number().int().positive("Service ID must be a positive integer"),
  date: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid date format"
  }),
  contact_name: z.string().min(1, "Contact name is required"),
  contact_email: z.string().email("Invalid email format"),
  contact_phone: z.string().optional(),
  notes: z.string().optional()
});

// Schema for anonymous appointment booking (without user account)
export const anonymousAppointmentSchema = z.object({
  // Patient information
  first_name: z.string().min(1, "First name is required").max(100, "First name must be less than 100 characters"),
  last_name: z.string().min(1, "Last name is required").max(100, "Last name must be less than 100 characters"),
  email: z.string().email("Invalid email format").max(255, "Email must be less than 255 characters"),
  phone: z.string().min(1, "Phone number is required").max(20, "Phone number must be less than 20 characters"),
  date_of_birth: z.string().optional(),
  gender: z.enum(['male', 'female', 'other']).optional(),
  address: z.string().optional(),
  city: z.string().max(100, "City must be less than 100 characters").optional(),
  postal_code: z.string().max(10, "Postal code must be less than 10 characters").optional(),
  country: z.string().max(100, "Country must be less than 100 characters").default('Romania'),
  emergency_contact_name: z.string().max(200, "Emergency contact name must be less than 200 characters").optional(),
  emergency_contact_phone: z.string().max(20, "Emergency contact phone must be less than 20 characters").optional(),
  medical_notes: z.string().optional(),
  allergies: z.string().optional(),
  
  // Appointment information
  employee_id: z.number().int().positive("Employee ID must be a positive integer"),
  service_id: z.number().int().positive("Service ID must be a positive integer"),
  start_time: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid start time"
  }),
  end_time: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid end time"
  }),
  notes: z.string().optional(),
});

export const confirmAppointmentSchema = z.object({
  token: z.string().min(1, "Verification token is required")
});

// ============================================================================
// REFERRAL TRACKING TYPES
// ============================================================================

/**
 * Valid referral sources for tracking
 */
export const ReferralSource = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  GOOGLE: 'google',
  LINKEDIN: 'linkedin',
  TIKTOK: 'tiktok',
  YOUTUBE: 'youtube',
  TWITTER: 'twitter',
  X: 'x'
} as const;

export type ReferralSourceType = typeof ReferralSource[keyof typeof ReferralSource];

/**
 * Interface for referral tracking data in session
 */
export interface ReferralTrackingData {
  referralSource: ReferralSourceType | null;
  detectedAt: string;
  savedAt?: string;
}

/**
 * Interface for session data with referral source
 */
export interface SessionDataWithReferral {
  sessionId: string;
  userAgent?: string;
  timestamp: string;
  referralSource?: ReferralSourceType | null;
  appName?: string;
}

/**
 * Schema for referral source validation
 */
export const referralSourceSchema = z.enum([
  ReferralSource.FACEBOOK,
  ReferralSource.INSTAGRAM,
  ReferralSource.GOOGLE,
  ReferralSource.LINKEDIN,
  ReferralSource.TIKTOK,
  ReferralSource.YOUTUBE,
  ReferralSource.TWITTER,
  ReferralSource.X
]);

/**
 * Schema for saving referral source request
 */
export const saveReferralSourceSchema = z.object({
  referralSource: referralSourceSchema.nullable()
});

export type AvailabilityQuery = z.infer<typeof availabilityQuerySchema>;
export type PublicAppointment = z.infer<typeof publicAppointmentSchema>;
export type AnonymousAppointment = z.infer<typeof anonymousAppointmentSchema>;
export type ConfirmAppointment = z.infer<typeof confirmAppointmentSchema>;

// Employee Services interfaces and schemas
export interface EmployeeService {
  id: number;
  employee_id: number;
  service_id: number;
  is_primary: boolean;
  experience_years: number;
  certification?: string;
  notes?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface InsertEmployeeService {
  employee_id: number;
  service_id: number;
  is_primary?: boolean;
  experience_years?: number;
  certification?: string;
  notes?: string;
  active?: boolean;
}

export interface UpdateEmployeeService {
  is_primary?: boolean;
  experience_years?: number;
  certification?: string;
  notes?: string;
  active?: boolean;
}

// Employee Services validation schemas - base schema without refine
const employeeServiceBaseSchema = z.object({
  employee_id: z.number().int().positive("Employee ID must be a positive integer"),
  service_id: z.number().int().positive("Service ID must be a positive integer"),
  is_primary: z.boolean().default(false),
  experience_years: z.number().int().min(0).max(50).default(0),
  certification: z.string().max(255).optional(),
  notes: z.string().max(1000).optional(),
  active: z.boolean().default(true)
});

export const insertEmployeeServiceSchema = employeeServiceBaseSchema.refine(async (data) => {
  // Business rule: An employee can have maximum 3 primary services
  if (data.is_primary) {
    // This validation will be checked in the service layer
    return true;
  }
  return true;
}, {
  message: "An employee can have maximum 3 primary services",
  path: ["is_primary"]
});

export const updateEmployeeServiceSchema = employeeServiceBaseSchema.partial();

export type InsertEmployeeServiceType = z.infer<typeof insertEmployeeServiceSchema>;
export type UpdateEmployeeServiceType = z.infer<typeof updateEmployeeServiceSchema>;

// Appointment Assistant Types
export interface AppointmentData {
  service?: string;
  specialist?: string;
  name?: string;
  email?: string;
  phone?: string;
  preferredDate?: string;
  preferredTime?: string;
}

export interface AppointmentContext {
  employees: Array<{
    id: number;
    name: string;
    surname: string;
    specialty?: string;
    photo_url?: string;
    services: Array<{
      id: number;
      name: string;
      category: string;
      price: number;
      duration: number;
    }>;
    availability: Array<{
      dayOfWeek: number;
      startTime: string;
      endTime: string;
    }>;
  }>;
  services: Service[];
}

// Service Images - Multiple images per service (max 6)
export interface ServiceImage {
  id: number;
  service_id: number;
  image_url: string;
  alt_text?: string;
  caption?: string;
  sort_order: number;
  is_primary: boolean;
  created_at: string;
  updated_at: string;
}

export interface InsertServiceImage {
  service_id: number;
  image_url: string;
  alt_text?: string;
  caption?: string;
  sort_order?: number;
  is_primary?: boolean;
}

// Zod schema for service image validation
export const insertServiceImageSchema = z.object({
  service_id: z.number().int().positive(),
  image_url: z.string().url(),
  alt_text: z.string().optional(),
  caption: z.string().optional(),
  sort_order: z.number().int().min(0).optional(),
  is_primary: z.boolean().optional(),
});

// --- Reservation System Types for lodging_days module ---

// Room interface
export interface Room {
  id: number;
  service_id: number;
  room_number: string;
  room_type?: string;
  capacity: number;
  status: 'active' | 'maintenance' | 'inactive';
  floor_number?: number;
  description?: string;
  images?: Array<{url: string, alt?: string, order?: number}>;
  created_at: string;
  updated_at: string;
}

// Reservation interface
// Client interfaces (for lodging_days)
export interface Client {
  id: number;
  user_id?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  document_type?: 'id_card' | 'passport' | 'driver_license' | 'other';
  document_number?: string;
  nationality?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relation?: string;
  preferences?: Record<string, any>;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface ClientReservation {
  id: number;
  client_id: number;
  reservation_id: number;
  is_primary_client: boolean;
  created_at: string;
}

// Insert types for clients
export interface InsertClient {
  user_id?: string;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  document_type?: 'id_card' | 'passport' | 'driver_license' | 'other';
  document_number?: string;
  nationality?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relation?: string;
  preferences?: Record<string, any>;
  notes?: string;
  is_active?: boolean;
  created_by?: string;
  updated_by?: string;
}

export interface UpdateClient {
  user_id?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  gender?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  document_type?: 'id_card' | 'passport' | 'driver_license' | 'other';
  document_number?: string;
  nationality?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relation?: string;
  preferences?: Record<string, any>;
  notes?: string;
  is_active?: boolean;
  updated_by?: string;
}

export interface InsertClientReservation {
  client_id: number;
  reservation_id: number;
  is_primary_client?: boolean;
}

// Extended types with relationships
export interface ClientWithDetails extends Client {
  user?: User;
  reservations?: Reservation[];
  client_reservations?: ClientReservation[];
}

export interface ClientReservationWithDetails extends ClientReservation {
  client?: Client;
  reservation?: Reservation;
}

export interface Reservation {
  id: number;
  room_id: number;
  patient_id?: number; // Keep for backward compatibility
  client_id?: number;  // New field for lodging clients
  user_id?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  check_in_date: string;
  check_out_date: string;
  status: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show';
  total_price?: number;
  deposit_amount: number;
  payment_status: 'pending' | 'partial' | 'paid' | 'refunded';
  notes?: string;
  special_requests?: string;
  adults_count: number;
  children_count: number;
  created_at: string;
  updated_at: string;
  confirmed_at?: string;
  cancelled_at?: string;
  cancelled_reason?: string;
  checked_in_at?: string;
  checked_out_at?: string;
  actual_check_in_date?: string;
  actual_check_out_date?: string;
}

// Room availability interface
export interface RoomAvailability {
  id: number;
  room_id: number;
  date: string;
  is_available: boolean;
  price_override?: number;
  min_stay_nights: number;
  max_stay_nights?: number;
  check_in_allowed: boolean;
  check_out_allowed: boolean;
  created_at: string;
  updated_at: string;
}

// Room availability exception interface
export interface RoomAvailabilityException {
  id: number;
  room_id: number;
  exception_type: 'maintenance' | 'blocked' | 'seasonal_rate' | 'event' | 'cleaning';
  start_date: string;
  end_date: string;
  reason?: string;
  is_all_day: boolean;
  price_override?: number;
  allow_check_in: boolean;
  allow_check_out: boolean;
  created_at: string;
  updated_at: string;
}

// Reservation guest interface
export interface ReservationGuest {
  id: number;
  reservation_id: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  document_type?: 'passport' | 'id_card' | 'driver_license' | 'other';
  document_number?: string;
  document_series?: string;
  nationality?: string;
  gender?: 'male' | 'female' | 'other';
  country_of_residence?: string;
  is_primary_guest: boolean;
  created_at: string;
}

// Insert types
export interface InsertRoom {
  service_id: number;
  room_number: string;
  room_type?: string;
  capacity?: number;
  status?: 'active' | 'maintenance' | 'inactive';
  floor_number?: number;
  description?: string;
}

export interface InsertReservation {
  room_id: number;
  patient_id?: number;
  user_id?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  check_in_date: string;
  check_out_date: string;
  status?: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show';
  total_price?: number;
  deposit_amount?: number;
  payment_status?: 'pending' | 'partial' | 'paid' | 'refunded';
  notes?: string;
  special_requests?: string;
  adults_count?: number;
  children_count?: number;
}

export interface InsertRoomAvailability {
  room_id: number;
  date: string;
  is_available?: boolean;
  price_override?: number;
  min_stay_nights?: number;
  max_stay_nights?: number;
  check_in_allowed?: boolean;
  check_out_allowed?: boolean;
}

export interface InsertRoomAvailabilityException {
  room_id: number;
  exception_type: 'maintenance' | 'blocked' | 'seasonal_rate' | 'event' | 'cleaning';
  start_date: string;
  end_date: string;
  reason?: string;
  is_all_day?: boolean;
  price_override?: number;
  allow_check_in?: boolean;
  allow_check_out?: boolean;
}

export interface InsertReservationGuest {
  reservation_id: number;
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  document_type?: 'passport' | 'id_card' | 'driver_license' | 'other';
  document_number?: string;
  document_series?: string;
  nationality?: string;
  gender?: 'male' | 'female' | 'other';
  country_of_residence?: string;
  is_primary_guest?: boolean;
}

// Update types
export interface UpdateRoom {
  room_number?: string;
  room_type?: string;
  capacity?: number;
  status?: 'active' | 'maintenance' | 'inactive';
  floor_number?: number;
  description?: string;
}

export interface UpdateReservation {
  room_id?: number;
  patient_id?: number;
  user_id?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  check_in_date?: string;
  check_out_date?: string;
  status?: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show';
  total_price?: number;
  deposit_amount?: number;
  payment_status?: 'pending' | 'partial' | 'paid' | 'refunded';
  notes?: string;
  special_requests?: string;
  adults_count?: number;
  children_count?: number;
  cancelled_reason?: string;
  checked_in_at?: string;
  checked_out_at?: string;
  actual_check_in_date?: string;
  actual_check_out_date?: string;
}

// Validation schemas
export const insertRoomSchema = z.object({
  service_id: z.number().int().positive(),
  room_number: z.string().min(1, "Room number is required").max(50),
  room_type: z.string().max(100).optional(),
  capacity: z.number().int().min(1).default(1),
  status: z.enum(['active', 'maintenance', 'inactive']).default('active'),
  floor_number: z.number().int().optional(),
  description: z.string().optional(),
});

export const updateRoomSchema = insertRoomSchema.partial();

// Room availability response for API
export interface RoomAvailabilityResponse {
  room_id: number;
  room_number: string;
  available: boolean;
  total_price?: number;
  nights?: number;
  price_per_night?: number;
  message?: string;
}

// Calendar availability response
export interface CalendarAvailabilityDay {
  date: string;
  available: boolean;
  price?: number;
  rooms_available: number;
}

export interface CalendarAvailabilityResponse {
  month: string;
  days: CalendarAvailabilityDay[];
}

// Public reservation request
export interface PublicReservationRequest {
  room_id: number;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
  check_in_date: string;
  check_out_date: string;
  adults_count: number;
  children_count?: number;
  special_requests?: string;
  notes?: string;
}

// Service with rooms response
export interface ServiceWithRooms extends Service {
  rooms: Room[];
}

export const insertReservationSchema = z.object({
  room_id: z.number().int().positive(),
  patient_id: z.number().int().positive().optional(),
  user_id: z.string().uuid().optional(),
  contact_name: z.string().max(255).optional(),
  contact_email: z.string().email().max(255).optional(),
  contact_phone: z.string().max(50).optional(),
  check_in_date: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid check-in date"
  }),
  check_out_date: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid check-out date"
  }),
  status: z.enum(['pending', 'confirmed', 'checked_in', 'checked_out', 'cancelled', 'no_show']).default('pending'),
  total_price: z.number().int().min(0).optional(),
  deposit_amount: z.number().int().min(0).default(0),
  payment_status: z.enum(['pending', 'partial', 'paid', 'refunded']).default('pending'),
  notes: z.string().optional(),
  special_requests: z.string().optional(),
  adults_count: z.number().int().min(1).default(1),
  children_count: z.number().int().min(0).default(0),
}).refine((data) => {
  const checkIn = new Date(data.check_in_date);
  const checkOut = new Date(data.check_out_date);
  return checkOut > checkIn;
}, {
  message: "Check-out date must be after check-in date",
  path: ["check_out_date"]
});

export const updateReservationSchema = z.object({
  room_id: z.number().int().positive().optional(),
  patient_id: z.number().int().positive().optional(),
  client_id: z.number().int().positive().optional(),
  user_id: z.string().uuid().optional(),
  contact_name: z.string().max(255).optional(),
  contact_email: z.string().email().max(255).optional(),
  contact_phone: z.string().max(50).optional(),
  check_in_date: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid check-in date"
  }).optional(),
  check_out_date: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid check-out date"
  }).optional(),
  status: z.enum(['pending', 'confirmed', 'checked_in', 'checked_out', 'cancelled', 'no_show']).optional(),
  total_price: z.number().int().min(0).optional(),
  deposit_amount: z.number().int().min(0).optional(),
  payment_status: z.enum(['pending', 'partial', 'paid', 'refunded']).optional(),
  notes: z.string().optional(),
  special_requests: z.string().optional(),
  adults_count: z.number().int().min(1).optional(),
  children_count: z.number().int().min(0).optional(),
});

export const insertRoomAvailabilitySchema = z.object({
  room_id: z.number().int().positive(),
  date: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid date"
  }),
  is_available: z.boolean().default(true),
  price_override: z.number().int().min(0).optional(),
  min_stay_nights: z.number().int().min(1).default(1),
  max_stay_nights: z.number().int().min(1).optional(),
  check_in_allowed: z.boolean().default(true),
  check_out_allowed: z.boolean().default(true),
});

export const updateRoomAvailabilitySchema = insertRoomAvailabilitySchema.partial();

export const insertRoomAvailabilityExceptionSchema = z.object({
  room_id: z.number().int().positive(),
  exception_type: z.enum(['maintenance', 'blocked', 'seasonal_rate', 'event', 'cleaning']),
  start_date: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid start date"
  }),
  end_date: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: "Invalid end date"
  }),
  reason: z.string().optional(),
  is_all_day: z.boolean().default(true),
  price_override: z.number().int().min(0).optional(),
  allow_check_in: z.boolean().default(false),
  allow_check_out: z.boolean().default(false),
}).refine((data) => {
  const startDate = new Date(data.start_date);
  const endDate = new Date(data.end_date);
  return endDate >= startDate;
}, {
  message: "End date must be after or equal to start date",
  path: ["end_date"]
});

export const insertReservationGuestSchema = z.object({
  reservation_id: z.number().int().positive(),
  first_name: z.string().min(1, "First name is required").max(255),
  last_name: z.string().min(1, "Last name is required").max(255),
  email: z.string().email().max(255).optional(),
  phone: z.string().max(50).optional(),
  date_of_birth: z.string().optional(),
  document_type: z.enum(['passport', 'id_card', 'driver_license', 'other']).optional(),
  document_number: z.string().max(100).optional(),
  document_series: z.string().max(50).optional(),
  nationality: z.string().max(100).optional(),
  gender: z.enum(['male', 'female', 'other']).optional(),
  country_of_residence: z.string().max(100).optional(),
  is_primary_guest: z.boolean().default(false),
});

// Client Zod schemas
export const insertClientSchema = z.object({
  user_id: z.string().uuid().optional(),
  first_name: z.string().min(1, "First name is required").max(255),
  last_name: z.string().min(1, "Last name is required").max(255),
  email: z.string().email().max(255).optional(),
  phone: z.string().max(50).optional(),
  date_of_birth: z.string().optional(),
  gender: z.string().max(20).optional(),
  address: z.string().optional(),
  city: z.string().max(100).optional(),
  postal_code: z.string().max(20).optional(),
  country: z.string().max(100).default('Romania'),
  document_type: z.enum(['id_card', 'passport', 'driver_license', 'other']).optional(),
  document_number: z.string().max(100).optional(),
  nationality: z.string().max(100).optional(),
  emergency_contact_name: z.string().max(255).optional(),
  emergency_contact_phone: z.string().max(50).optional(),
  emergency_contact_relation: z.string().max(100).optional(),
  preferences: z.record(z.any()).optional(),
  notes: z.string().optional(),
  is_active: z.boolean().default(true),
  created_by: z.string().uuid().optional(),
  updated_by: z.string().uuid().optional(),
});

export const updateClientSchema = insertClientSchema.partial().omit({ created_by: true });

export const insertClientReservationSchema = z.object({
  client_id: z.number().int().positive(),
  reservation_id: z.number().int().positive(),
  is_primary_client: z.boolean().default(false),
});

// Export types
export type InsertRoomType = z.infer<typeof insertRoomSchema>;
export type InsertReservationType = z.infer<typeof insertReservationSchema>;
export type InsertRoomAvailabilityType = z.infer<typeof insertRoomAvailabilitySchema>;
export type InsertRoomAvailabilityExceptionType = z.infer<typeof insertRoomAvailabilityExceptionSchema>;
export type InsertReservationGuestType = z.infer<typeof insertReservationGuestSchema>;

// Extended types with relations
export interface ReservationWithDetails extends Reservation {
  room: Room & {
    service: {
      id: number;
      name: string;
      description: string;
      price: number;
      service_type?: 'clinic_hours' | 'lodging_days' | 'homepage_cards' | 'website_presentation';
      image_url?: string | null;
    };
  };
  client?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  guests: ReservationGuest[];
}

export interface RoomWithDetails extends Room {
  service: {
    id: number;
    name: string;
    description: string;
    price: number;
    service_type?: 'clinic_hours' | 'lodging_days' | 'homepage_cards' | 'website_presentation';
    image_url?: string | null;
  };
  reservations_count: number;
  current_reservation?: Reservation;
}

// Calendar event types for reservation calendar
export interface ReservationCalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  extendedProps: {
    type: 'reservation' | 'exception' | 'maintenance';
    reservationId?: number;
    roomId: number;
    status?: string;
    exceptionType?: string;
  };
}